// What the last sign-in attempt did, as the server left it on the URL.
//
// The client's half of the one marker `api/src/routes/auth.routes.ts` writes.
// Every other way a sign-in can fail sends the browser back to `/` bare, because
// every other way explains itself: somebody who pressed Cancel knows they did,
// and somebody whose handshake expired gets a working button. A server at its
// account cap (migration 0003) explains nothing at all - the button works,
// Google says yes, and they land back on the same screen - so that outcome, and
// so far only that outcome, arrives named.
//
// THIS IS NOT AN API CALL, which is why it is here rather than in `api/`. There
// is no request and no envelope: the browser was redirected, and the answer is
// already in the address bar by the time any code runs.
//
// It DECORATES the gate, it does not raise it. Whether somebody is signed out is
// decided by a 401 and nothing else (`useDayScreen`); a marker that could put the
// gate up on its own would be a URL anybody could paste to sign somebody out.
//
// And it BELONGS TO THE ATTEMPT, NOT TO THE ADDRESS, which is what
// `forgetSignInOutcome` below is for. A marker left on the URL is re-read on
// every later arrival at it - a reload, a Back, a bookmark, a link somebody
// pastes into a chat - and a sentence that was true once is indistinguishable
// from one that is true now: a stranger opening a shared `/?signin=full` is told
// the app is not taking accounts when a place may well have come free, and the
// sentence is convincing enough that they will not press the button that would
// have shown them otherwise. The gate reads it and then clears it, so it is said
// once, to the person it happened to. It vanishing on a manual reload is
// CORRECT and is not a cost: a reload is not a fresh sign-up attempt.

/** The one key the server writes and this client reads. Spelled once. */
const MARKER = 'signin';

/**
 * The outcomes a screen has words for. `null` covers the ordinary arrival -
 * nobody has tried yet, or the marker is one this client does not know, which is
 * an older client meeting a newer server and must read as "nothing to say"
 * rather than as a blank line where a sentence should be.
 */
export type SignInOutcome = 'accounts-full' | null;

/**
 * Read the marker off a query string.
 *
 * Takes the search string rather than reaching for `window`, so it is the same
 * function under test as in the browser.
 */
export function signInOutcome(search: string = window.location.search): SignInOutcome {
  return new URLSearchParams(search).get(MARKER) === 'full' ? 'accounts-full' : null;
}

/**
 * Take the marker back off the address, once whoever reads it has read it.
 *
 * `replaceState` and never `pushState`: the refused arrival is not somewhere to
 * go Back to. Only the one key goes - another marker, or a campaign parameter on
 * the link somebody followed, is none of this module's business and survives
 * untouched. Reaches for `window` because rewriting history is the whole of what
 * it does; `signInOutcome` above is the half that stays testable in isolation.
 */
export function forgetSignInOutcome(): void {
  const url = new URL(window.location.href);
  if (!url.searchParams.has(MARKER)) return;
  url.searchParams.delete(MARKER);
  window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
}
